import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { cropImageFile } from '../utils/cropImage';

const RATIOS = [
  { id: '1:1', label: '1:1', value: 1 },
  { id: '4:3', label: '4:3', value: 4 / 3 },
  { id: '3:4', label: '3:4', value: 3 / 4 },
  { id: '16:9', label: '16:9', value: 16 / 9 },
  { id: 'livre', label: 'Livre', value: null },
];
const MIN_FRAME = 64;
const initialRatio = (aspect) => (RATIOS.find((r) => r.value === aspect) || RATIOS[0]).id;

// Recorta uma fila de arquivos, um por vez. `onDone(files)` recebe os arquivos
// finais (recortados ou, se pulados, os originais); `onCancel()` aborta tudo.
export default function ImageCropModal({ files, aspect = 1, allowSkip = false, onDone, onCancel }) {
  const [index, setIndex] = useState(0);
  const [results, setResults] = useState([]);
  const [ratioId, setRatioId] = useState(() => initialRatio(aspect));
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [frame, setFrame] = useState(null); // tamanho do quadro (px), só usado no modo Livre
  const [autoFrame, setAutoFrame] = useState(null);
  const [busy, setBusy] = useState(false);
  const stageRef = useRef(null);
  const drag = useRef(null);

  const file = files[index];
  const src = useMemo(() => URL.createObjectURL(file), [file]);
  useEffect(() => () => URL.revokeObjectURL(src), [src]);

  const ratio = RATIOS.find((r) => r.id === ratioId);
  const free = ratio.value === null;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  const selectRatio = (r) => {
    // Ao entrar no modo Livre, parte do quadro que já estava na tela.
    if (r.value === null && autoFrame) setFrame(autoFrame);
    setRatioId(r.id);
    setCrop({ x: 0, y: 0 });
  };

  const next = useCallback((out) => {
    const all = [...results, out];
    if (index + 1 >= files.length) { onDone(all); return; }
    setResults(all);
    setIndex(index + 1);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setArea(null);
    setFrame(null);
    setRatioId(initialRatio(aspect));
  }, [results, index, files.length, aspect, onDone]);

  const apply = async () => {
    if (!area || busy) return;
    setBusy(true);
    try {
      next(await cropImageFile(file, area, src));
    } catch (err) {
      alert(err.message);
    } finally {
      setBusy(false);
    }
  };

  // Alças do modo Livre: o quadro cresce/encolhe simetricamente a partir do centro.
  const startDrag = (axis) => (e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { axis, x: e.clientX, y: e.clientY, w: frame.width, h: frame.height };
  };
  const moveDrag = (e) => {
    const d = drag.current;
    if (!d) return;
    const box = stageRef.current.getBoundingClientRect();
    const clamp = (v, max) => Math.round(Math.min(max, Math.max(MIN_FRAME, v)));
    setFrame({
      width: d.axis.includes('x') ? clamp(d.w + (e.clientX - d.x) * 2, box.width) : d.w,
      height: d.axis.includes('y') ? clamp(d.h + (e.clientY - d.y) * 2, box.height) : d.h,
    });
  };
  const endDrag = () => { drag.current = null; };
  const dragProps = (axis) => ({ onPointerDown: startDrag(axis), onPointerMove: moveDrag, onPointerUp: endDrag, onPointerCancel: endDrag });

  const handleCls = 'absolute z-10 bg-paper border border-navy rounded-sm touch-none -translate-x-1/2 -translate-y-1/2';

  return (
    <div className="fixed inset-0 bg-navy/50 backdrop-blur-[2px] z-[200] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Recortar imagem">
      <div className="bg-paper rounded-card p-5 w-full max-w-lg shadow-xl border border-line">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-display text-xl font-bold text-navy tracking-tight">Recortar imagem</h2>
          {files.length > 1 && <span className="text-xs font-semibold text-muted uppercase tracking-widest">{index + 1} de {files.length}</span>}
        </div>
        <div ref={stageRef} className="relative w-full h-[50vh] max-h-[420px] bg-navy rounded-card overflow-hidden touch-none">
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            minZoom={1}
            maxZoom={5}
            aspect={free && autoFrame ? autoFrame.width / autoFrame.height : (ratio.value || 1)}
            cropSize={free && frame ? frame : undefined}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropSizeChange={setAutoFrame}
            onCropComplete={(_, px) => setArea(px)}
            objectFit="contain"
          />
          {free && frame && (
            <>
              <div {...dragProps('x')} aria-label="Ajustar largura" className={`${handleCls} w-3 h-10 cursor-ew-resize`} style={{ top: '50%', left: `calc(50% + ${frame.width / 2}px)` }} />
              <div {...dragProps('y')} aria-label="Ajustar altura" className={`${handleCls} h-3 w-10 cursor-ns-resize`} style={{ left: '50%', top: `calc(50% + ${frame.height / 2}px)` }} />
              <div {...dragProps('xy')} aria-label="Ajustar largura e altura" className={`${handleCls} w-5 h-5 cursor-nwse-resize`} style={{ left: `calc(50% + ${frame.width / 2}px)`, top: `calc(50% + ${frame.height / 2}px)` }} />
            </>
          )}
        </div>
        <div className="flex flex-wrap gap-2 mt-4" role="group" aria-label="Proporção">
          {RATIOS.map((r) => (
            <button key={r.id} type="button" onClick={() => selectRatio(r)} aria-pressed={r.id === ratioId} className={`px-3 min-h-[44px] rounded-card border text-xs font-semibold transition-colors ${r.id === ratioId ? 'bg-navy text-white border-navy' : 'bg-paper text-ink border-line hover:border-navy'}`}>{r.label}</button>
          ))}
        </div>
        <input type="range" min="1" max="5" step="0.01" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} aria-label="Zoom" className="w-full mt-4 accent-navy" />
        <div className="flex justify-end gap-3 mt-5">
          {allowSkip && <button type="button" onClick={() => next(file)} disabled={busy} className="px-4 min-h-[44px] text-sm font-semibold text-muted hover:text-ink transition-colors disabled:opacity-50">Pular</button>}
          <button type="button" onClick={onCancel} disabled={busy} className="px-5 min-h-[44px] rounded-card border border-line text-sm font-semibold text-ink hover:border-navy transition-colors disabled:opacity-50">Cancelar</button>
          <button type="button" onClick={apply} disabled={busy || !area} className="px-5 min-h-[44px] rounded-card bg-navy text-white text-sm font-semibold hover:bg-navy-2 transition-colors disabled:opacity-50">Aplicar</button>
        </div>
      </div>
    </div>
  );
}
